import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import styled from '@emotion/styled';
import {
  spacing,
  mediaMax,
  fontSize,
  fontWeight,
} from 'presentations/views/styles';
import ServicePageTemplate from 'presentations/views/components/templates/ServicePageTemplate';
import { useCustomerStoryListPage } from './hooks';
import Breadcrumbs from 'presentations/views/components/organisms/Breadcrumbs';
import Icon from 'presentations/views/components/atoms/Icon';
import Link from 'presentations/views/components/atoms/Link';
import { ReactNode, useMemo } from 'react';
import { ChangelogListItem } from 'presentations/api/schema/contents';
import Pagination, {
  Props as PaginationProps,
} from 'presentations/views/components/atoms/Pagination';
import TopTitle from '../ResourceListPage/TopTitle';
import { black, gray03, white } from 'presentations/views/styles/color';
import { formatToDate } from 'infras/datetime';
import { BodyContainer } from '../../organisms/BlogContent';
import { changelogPath } from 'presentations/routes/path';

export interface Props {
  title: string;
  changelogs: ChangelogListItem[];
  paginationProps: PaginationProps;
  breadcrumbs: { title: string; link: string }[];
  header?: ReactNode;
}

export default function Component(props: Props) {
  const { state, actions } = useCustomerStoryListPage({
    breadcrumbs: props.breadcrumbs,
  });
  const t = useMemo(() => translateInComponent(translations), [translations]);
  return (
    <ServicePageTemplate
      breadcrumbs={<Breadcrumbs routes={state.routes} />}
      header={props.header}
    >
      <Container>
        <TopTitle title={props.title} description={t('description')} />
        <List>
          {props.changelogs.map((changelog) => (
            <ListItem key={changelog.slug}>
              <TimelineContainer>
                <TimelineMark>
                  <TimelineMarkIcon name="check" />
                </TimelineMark>
                <TimelineLine />
              </TimelineContainer>
              <ListItemContent>
                <DateContent>{formatToDate(changelog.sortDate)}</DateContent>
                <Title href={changelogPath(changelog.slug)}>
                  {changelog.title}
                </Title>
                <Description
                  dangerouslySetInnerHTML={{ __html: changelog.content }}
                />
                <More href={changelogPath(changelog.slug)}>
                  {t('Read more')}
                  <MoreIcon name="chevron-right" />
                </More>
              </ListItemContent>
            </ListItem>
          ))}
        </List>
        <PaginationContainer>
          <Pagination {...props.paginationProps} />
        </PaginationContainer>
      </Container>
    </ServicePageTemplate>
  );
}

const Container = styled.div`
  max-width: 912px;
  margin: ${spacing(64)} auto ${spacing(80)};

  ${mediaMax.desktop} {
    margin: 0 ${spacing(24)};
  }
  ${mediaMax.tablet} {
    flex-direction: column;
    margin: ${spacing(24)} 0;
  }
  ${mediaMax.xs} {
    width: 100%;
  }
`;

const List = styled.div`
  ${mediaMax.tablet} {
    padding: 0 ${spacing(12)};
  }
`;
const ListItem = styled.div`
  display: flex;
`;

const TimelineContainer = styled.div`
  width: 50px;
  display: flex;
  flex-direction: column;
  align-items: center;
  ${mediaMax.tablet} {
    display: none;
  }
`;
const ListItemContent = styled.div`
  flex: 1;
  margin-bottom: ${spacing(48)};
`;
const DateContent = styled.div`
  font-size: ${fontSize(16)};
  margin-bottom: ${spacing(4)};
`;
const Title = styled(Link)`
  color: ${black};
  display: block;
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(26)};
  border-bottom: 1px solid ${black};
  margin-bottom: ${spacing(24)};
  padding-bottom: ${spacing(4)};
  line-height: 1.36;
`;
const Description = styled(BodyContainer)`
  font-size: ${fontSize(16)};
  margin: 0;
  img {
    max-width: 480px;
    width: 100%;
    ${mediaMax.tablet} {
      max-width: auto;
    }
  }
`;
const TimelineMark = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${black};
  margin: ${spacing(2)} 0;
`;
const TimelineLine = styled.div`
  height: 100%;
  border-right: 1px solid ${gray03};
  flex: 1;
`;
const TimelineMarkIcon = styled(Icon)`
  width: 14px !important;
  height: 14px !important;
  color: ${white};
`;
const More = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: ${fontSize(16)};
  margin-top: ${spacing(8)};
`;
const MoreIcon = styled(Icon)`
  color: ${black};
  margin-left: ${spacing(2)};
`;

const PaginationContainer = styled.div`
  padding: ${spacing(56)} 0 0 0;
  display: flex;
  justify-content: center;

  ${mediaMax.tablet} {
    padding: ${spacing(20)} 0 0 0;
  }
`;
